import { useEffect, useState } from "react"
import { ExamService } from "../../../Services/examServices"
import moment from "moment"
import EditUserInfoModal from "../../../components/Modal/EditUserInfo.modal"
import { useNavigate } from "react-router-dom"
import { defaultTheme, PageShell, TopBar, NxButton, NxIcon } from "../../../components/nx"
import {
    DocItem,
    DocRow,
    DocumentPreviewModal,
    docItemsFromProfile,
} from "../../../components/QualificationDocs/QualificationDocs"
import { ApprovalNotice, approvalStatusOf } from "../../../components/ApprovalNotice/ApprovalNotice"

const t = defaultTheme;

const FALLBACK_AVATAR = "https://www.pngitem.com/pimgs/m/581-5813504_avatar-dummy-png-transparent-png.png";

export default function Profile() {
    const navigate = useNavigate();

    const [profile, setProfile] = useState<any>();
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [previewDoc, setPreviewDoc] = useState<DocItem | null>(null);

    const qualificationDocs = docItemsFromProfile(profile?.qualificationDocs, profile?.qualificationDoc);
    const approvalStatus = approvalStatusOf(profile);

    const getProfile = async () => {
        await ExamService.getUserInfoWtihoutExams().then((res) => {
            if (res.status === 200) {
                setProfile(res.data.data.studentInfo[0])
            }
        })
    }

    useEffect(() => {
        getProfile();
    }, [])

    const fields: { label: string; value?: string }[] = [
        { label: "Username", value: profile?.username },
        { label: "Name", value: [profile?.name, profile?.last_name].filter(Boolean).join(" ") || undefined },
        { label: "Email", value: profile?.email },
        { label: "Date of Birth", value: profile?.dob ? moment(profile.dob).format("DD MMM YYYY") : undefined },
        { label: "Gender", value: profile?.gender },
        { label: "Mobile", value: profile?.mobile },
        { label: "City", value: profile?.city },
        { label: "State", value: profile?.state },
        { label: "Registration No.", value: profile?.registrationNumber },
        { label: "IRIA Member", value: profile?.isIriaMember ? "Yes" : "No" },
        { label: "Membership No.", value: profile?.membershipNumber },
        { label: "Qualification", value: profile?.qualification },
        {
            label: "Approval Status",
            value: profile
                ? approvalStatus === 'APPROVED' ? 'Approved'
                    : approvalStatus === 'REJECTED' ? 'Rejected'
                        : 'Pending approval'
                : undefined,
        },
    ];

    return (
        <PageShell t={t} style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <TopBar
                t={t}
                crumbs={[{ label: 'Dashboard', path: '/exam/dashboard' }, 'Profile']}
                onNavigate={(path) => navigate(path)}
                right={
                    <NxButton t={t} kind="secondary" onClick={() => setShowEditModal(true)} icon={NxIcon.doc(t.ink)}>
                        Edit
                    </NxButton>
                }
            />

            <div className="nx-page-body">
                <div style={{ maxWidth: 720, margin: '0 auto' }}>
                    {/* Back + heading */}
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            color: t.inkSoft, fontFamily: 'inherit', fontSize: 13, padding: 0, marginBottom: 18,
                        }}
                    >
                        {NxIcon.arrowLeft(t.inkSoft)} Back
                    </button>

                    {/* Approval status — carries the admin's reason when rejected */}
                    {profile && approvalStatus !== 'APPROVED' && (
                        <div style={{ marginBottom: 18 }}>
                            <ApprovalNotice t={t} profile={profile} />
                        </div>
                    )}

                    {/* Profile card */}
                    <div style={{
                        background: t.surface, border: `1px solid ${t.border}`, borderRadius: 14,
                        overflow: 'hidden',
                    }}>
                        {/* Header band */}
                        <div className="nx-profile-head" style={{
                            display: 'flex', alignItems: 'center', gap: 18,
                            padding: '26px 28px', borderBottom: `1px solid ${t.border}`,
                            background: t.paperAlt,
                        }}>
                            <img
                                src={profile?.image || FALLBACK_AVATAR}
                                alt="Profile"
                                style={{
                                    width: 84, height: 84, borderRadius: '50%', objectFit: 'cover',
                                    border: `1px solid ${t.border}`, background: t.surface, flexShrink: 0,
                                }}
                            />
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 22, fontWeight: 500, color: t.ink, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                                    {profile?.name || '—'}
                                </div>
                                <div style={{ fontSize: 13, color: t.inkMute, marginTop: 4, overflowWrap: 'anywhere' }}>
                                    {profile?.email || '—'}
                                </div>
                            </div>
                        </div>

                        {/* Detail rows */}
                        <div>
                            {fields.map((f, i) => (
                                <div key={f.label} className="nx-profile-row" style={{
                                    display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16,
                                    padding: '16px 28px', alignItems: 'center',
                                    borderBottom: `1px solid ${t.border}`,
                                }}>
                                    <span style={{
                                        fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                                        textTransform: 'uppercase', color: t.inkMute,
                                    }}>{f.label}</span>
                                    <span style={{ fontSize: 14, color: t.ink, fontWeight: 500, overflowWrap: 'anywhere' }}>
                                        {f.value || '—'}
                                    </span>
                                </div>
                            ))}
                            {/* Qualification documents */}
                            <div className="nx-profile-row" style={{
                                display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16,
                                padding: '16px 28px', alignItems: 'start',
                            }}>
                                <span style={{
                                    fontSize: 11, fontWeight: 600, letterSpacing: '0.04em',
                                    textTransform: 'uppercase', color: t.inkMute, marginTop: 8,
                                }}>Qualification Docs</span>
                                {qualificationDocs.length ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {qualificationDocs.map((doc) => (
                                            <DocRow key={doc.key} t={t} doc={doc} onView={setPreviewDoc} />
                                        ))}
                                    </div>
                                ) : (
                                    <span style={{ fontSize: 14, color: t.ink, fontWeight: 500 }}>—</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <DocumentPreviewModal t={t} doc={previewDoc} onClose={() => setPreviewDoc(null)} />

            <EditUserInfoModal
                show={showEditModal}
                handleClose={() => setShowEditModal(false)}
                data={profile}
                reload={getProfile}
            />
        </PageShell>
    )
}
